import { Injectable } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ApiService } from './api.service';
import { GetEntitlementsQuery, GetEntitlementsQueryVariables } from '../../../API';
import { GetEntitlements } from '../../../graphql/queries';
import { ROLE } from '../models/auth';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signInWithRedirect, signOut } from 'aws-amplify/auth';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public loginState = false;
  public errorState: { message: string } | null = null;
  private userInfo?: any;
  public entitlements: any[] = [];
  private userRole: string = '';

  public readonly groupRoleMap: Record<string, string> = {
    'nfl-dcr-portal-admins': ROLE.ADMIN,
    'nfl-dcr-portal-analysts': ROLE.ANALYST,
    'nfl-dcr-portal-bi-ml-specialists': ROLE.BI_ML_SPECIALIST,
  };

  constructor(
    private nzMessageService: NzMessageService,
    private apiService: ApiService,
  ) {}

  async logout() {
    await signOut();
    this.loginState = false;
  }

  /* @typescript-eslint/no-explicit-any */
  async fetchUserDetails(): Promise<boolean> {
    try {
      const user = await getCurrentUser();

      // Note that session will no longer contain refreshToken and clockDrift
      const { tokens: session, identityId } = await fetchAuthSession();

      const userGroups = (session?.accessToken?.payload['cognito:groups'] as string[]).filter(
        (group: string) => !group.endsWith('Okta'),
      );

      let attributes = await fetchUserAttributes();

      attributes = { orgId: attributes['custom:orgId'], ...attributes };
      delete attributes['custom:orgId'];

      this.userInfo = { ...user, ...attributes, identityId: identityId };

      await this.getEntitlements(userGroups[0]);
      this.setUserRole(userGroups);

      return true;
    } catch (err) {
      this.nzMessageService.error(`${err} while getting current user info`);
      return false;
    }
  }

  setUserRole(userGroups: string[] | null | undefined): void {
    if (!userGroups || userGroups.length === 0) {
      return;
    }

    const primaryGroup = userGroups[0];

    this.userRole = this.groupRoleMap[primaryGroup];
  }

  getUserInfo(): any {
    return this.userInfo;
  }

  getUserRole(): string {
    return this.userRole;
  }

  async validateLogin(): Promise<boolean> {
    try {
      const authSession = await fetchAuthSession({ forceRefresh: true });
      if (authSession.credentials) {
        this.loginState = true;
      }
      return true;
    } catch (_err) {
      console.log('_err', _err);
      return false;
    }
  }

  public federatedOktaSignIn(): void {
    signInWithRedirect({
      provider: { custom: 'Okta' },
      customState: '/',
    }).catch((err) => {
      console.error('err', err);
    });
  }

  public getEntitlements(roleName: string) {
    this.apiService
      .query<GetEntitlementsQuery, GetEntitlementsQueryVariables>(GetEntitlements, {
        role: roleName,
      })
      .then((result) => {
        if (result.data?.GetEntitlements?.entitlements) {
          this.entitlements.push(...result.data.GetEntitlements.entitlements);
        }
      });
  }

  public hasEntitlements(entitlements: string[]): boolean {
    return entitlements.some((entitlement) => this.entitlements.includes(entitlement));
  }
}
